import type { Household } from '../../lib/supabase'
import { graceEndsAt, hasSubscription, householdAccess, trialDaysLeft } from '../../lib/billing'
import { FREE_ITEM_LIMIT, PLAN_NAME, amountsFor, priceSummary } from '../../lib/pricing'
import { userLocale } from '../../lib/locale'
import type { Prices } from '../../hooks/usePrices'
import { Section } from './Section'

// Free or Plus (042): where the household stands, and the one thing to do
// about it. Only the paying member manages billing (/portal refuses anyone
// else); getting Plus for a household without a live subscription is open to
// any member.
export function SubscriptionSection({ household, memberId, prices, loading, onUpgrade, onManage }: {
  household: Household
  memberId: string | null
  prices: Prices
  loading: boolean
  onUpgrade: () => void
  onManage: () => void
}) {
  // Absent on a deployment that stopped its migrations before billing existed.
  if (household.subscription_status == null) return null
  const access = householdAccess(household)
  const subscribed = hasSubscription(household)
  const isPayer = memberId != null && household.payer_member_id === memberId
  const days = trialDaysLeft(household)
  const line = access === 'trialing'
    ? `Free trial, ${days} ${days === 1 ? 'day' : 'days'} left`
    : access === 'active'
      ? household.cancel_at_period_end
        ? `${PLAN_NAME}. Ends ${new Date(household.plan_expires_at).toLocaleDateString()}`
        : PLAN_NAME
      : access === 'grace'
        ? household.subscription_status === 'past_due' || household.subscription_status === 'unpaid'
          ? `Payment issue. Update your card to keep ${PLAN_NAME}`
          : `${PLAN_NAME} has ended. Renew to keep it`
        : `Free: the list, up to ${FREE_ITEM_LIMIT} items at a time`

  return (
    <Section title="Subscription">
      <div className="ml-settings__card">
        <p className={access === 'free' ? 'ml-settings__line ml-settings__line--warn' : 'ml-settings__line'}>{line}</p>
        {access === 'trialing' && (
          <p className="ml-settings__sub">
            Then Free, or {PLAN_NAME} to keep everything. Nothing is charged unless you choose {PLAN_NAME}.
          </p>
        )}
        {access === 'grace' && (
          <p data-testid="grace-banner" className="ml-settings__line--warn ml-settings__sub">
            The household moves to Free on {graceEndsAt(household).toLocaleDateString()}. Nothing is deleted.
          </p>
        )}
        {!subscribed && (
          <p className="ml-settings__sub">{PLAN_NAME}: {priceSummary(amountsFor(prices.table, prices.currency), prices.currency, userLocale())}.</p>
        )}
        {subscribed && !isPayer
          ? <p className="ml-settings__sub">Billing is managed by the list owner.</p>
          : (
            <button type="button" className="ml-text-btn" disabled={loading} onClick={subscribed ? onManage : onUpgrade}>
              {loading ? 'One moment…' : subscribed ? 'Manage subscription' : `Get ${PLAN_NAME}`}
            </button>
          )}
      </div>
    </Section>
  )
}
